import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { Reveal } from '@/components/motion/Reveal'

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('eyebrow', className)}>{children}</p>
}

/**
 * A page section with the shared vertical rhythm. `tone` switches the surface
 * without page-level overrides, which is what kept spacing inconsistent in the
 * previous build.
 */
export function Section({
  children,
  className,
  tone = 'default',
  id,
  labelledBy,
}: {
  children: ReactNode
  className?: string
  tone?: 'default' | 'surface' | 'ink'
  id?: string
  labelledBy?: string
}) {
  const tones = {
    default: 'bg-white',
    surface: 'bg-surface-2',
    ink: 'bg-ink text-edge-soft',
  } as const

  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      className={cn('py-16 sm:py-20 lg:py-24', tones[tone], className)}
    >
      <div className="shell">{children}</div>
    </section>
  )
}

export function SectionHeader({
  eyebrow,
  title,
  lede,
  action,
  id,
  className,
}: {
  eyebrow?: ReactNode
  title?: ReactNode
  lede?: ReactNode
  action?: ReactNode
  id?: string
  className?: string
}) {
  return (
    <div
      className={cn('flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between', className)}
    >
      <div className="max-w-2xl">
        {eyebrow ? (
          <Reveal variant="fade" duration={500}>
            <Eyebrow className="mb-3">{eyebrow}</Eyebrow>
          </Reveal>
        ) : null}
        {title ? (
          <h2
            id={id}
            className="text-balance text-[26px] font-extrabold leading-[1.15] tracking-[-0.02em] sm:text-[32px] lg:text-[38px]"
          >
            {title}
          </h2>
        ) : null}
        {lede ? (
          <p className="mt-4 max-w-measure text-pretty text-[15px] leading-[1.75] text-muted">
            {lede}
          </p>
        ) : null}
      </div>
      {action ? <div className="flex-shrink-0">{action}</div> : null}
    </div>
  )
}

/** The small monospace source references used throughout the design. */
export function SourceRef({ children }: { children: ReactNode }) {
  return (
    <p className="font-mono text-[10.5px] leading-relaxed tracking-[0.04em] text-muted-soft">
      {children}
    </p>
  )
}

export function Card({
  children,
  className,
  as: Tag = 'div',
}: {
  children: ReactNode
  className?: string
  as?: 'div' | 'article' | 'li'
}) {
  return (
    <Tag
      className={cn(
        'rounded-card border border-line bg-white p-6 shadow-card transition-shadow duration-200 ease-brand sm:p-7',
        className,
      )}
    >
      {children}
    </Tag>
  )
}
